import React, { useEffect, useRef, useState } from 'react';

export type Device = 'desktop' | 'mobile';

/** The width the desktop preview is laid out at. */
export const DESKTOP_WIDTH = 1280;

export function DeviceSwitch({
  device,
  onDevice,
}: {
  device: Device;
  onDevice: (d: Device) => void;
}) {
  return (
    <div className="pg-seg" role="radiogroup" aria-label="Preview size">
      {(['desktop', 'mobile'] as Device[]).map((d) => (
        <button
          key={d}
          type="button"
          role="radio"
          aria-checked={device === d}
          data-active={device === d ? 'true' : undefined}
          onClick={() => onDevice(d)}
        >
          {d === 'desktop' ? 'Desktop' : 'Mobile'}
        </button>
      ))}
    </div>
  );
}

/**
 * Desktop is drawn at a real desktop width and scaled down to fit, so it
 * never falls back to the site's phone layout in a narrow window.
 */
export function useFrameScale(device: Device) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapWidth, setWrapWidth] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWrapWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale =
    device === 'desktop' && wrapWidth && wrapWidth < DESKTOP_WIDTH ? wrapWidth / DESKTOP_WIDTH : 1;
  return { wrapRef, scale };
}

/** The frame's size for that scale. */
export function frameStyleFor(scale: number, height: number | string): React.CSSProperties {
  return scale < 1
    ? {
        width: DESKTOP_WIDTH,
        height: typeof height === 'number' ? height / scale : `calc((${height}) / ${scale})`,
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
      }
    : { height };
}
